/**
 * Frontend structured logger — JSON payloads, PII redaction, correlation IDs,
 * and optional shipping of warn+ logs to POST /api/logs.
 */

import { env } from '../config/env';
import { analyticsService } from '../services/analytics.service';
import {
  enqueueLogShip,
  LOG_SCHEMA_VERSION,
  setLogCorrelationIdProvider,
  type ShippableLogEntry,
} from './log-ship';
import { redactLogValue } from './log-redact';
import { resolveClientLogLevel, shouldClientLog, type ClientLogLevel } from './log-level';

export enum LogLevel {
  FATAL = 'fatal',
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
  DEBUG = 'debug',
  TRACE = 'trace',
}

type LogContext = Record<string, unknown>;

interface UserContext {
  userId?: string;
  tenantId?: string;
}

let userContextProvider: () => UserContext = () => ({});

export function setLogUserContextProvider(fn: () => UserContext): void {
  userContextProvider = fn;
}

setLogCorrelationIdProvider(() => analyticsService.getOperationId());

function serializeError(err: unknown): LogContext {
  if (err instanceof Error) {
    return {
      'error.message': err.message,
      'error.name': err.name,
      'error.stack': err.stack?.slice(0, 2000),
    };
  }
  if (err !== undefined && err !== null) {
    return { 'error.message': String(err) };
  }
  return {};
}

function buildPayload(
  level: ClientLogLevel,
  message: string,
  properties?: LogContext,
  error?: unknown
): Record<string, unknown> {
  const user = userContextProvider();
  const correlationId = analyticsService.getOperationId();
  const event =
    (typeof properties?.event === 'string' ? properties.event : undefined) ??
    (message.includes('.') && !message.includes(' ') ? message : `frontend.${level}`);

  const payload: Record<string, unknown> = {
    'log.schema': LOG_SCHEMA_VERSION,
    timestamp: new Date().toISOString(),
    level,
    event,
    message,
    env: env.nodeEnv,
    'service.name': 'creatoros-frontend',
    'service.version': env.appVersion,
    'correlation.id': correlationId,
    'user.id': user.userId,
    'tenant.id': user.tenantId ?? user.userId,
    'client.log_level': resolveClientLogLevel(),
  };

  if (properties) {
    for (const [key, value] of Object.entries(properties)) {
      if (value === undefined || value === null || key === 'event') continue;
      payload[key] = redactLogValue(value, key);
    }
  }

  if (error !== undefined) {
    Object.assign(payload, serializeError(error));
  }

  return payload;
}

function emitConsole(level: ClientLogLevel, payload: Record<string, unknown>): void {
  const line = JSON.stringify(payload);
  switch (level) {
    case 'fatal':
    case 'error':
      console.error(line);
      break;
    case 'warn':
      console.warn(line);
      break;
    case 'debug':
    case 'trace':
      console.debug(line);
      break;
    default:
      console.info(line);
  }
}

function write(
  level: ClientLogLevel,
  message: string,
  error?: unknown,
  properties?: LogContext
): void {
  if (!shouldClientLog(level)) return;

  const payload = buildPayload(level, message, properties, error);
  emitConsole(level, payload);

  const shipEntry: ShippableLogEntry = {
    level,
    event: String(payload.event),
    message,
    fields: payload,
    ts: String(payload.timestamp),
  };
  enqueueLogShip(shipEntry);

  if (error instanceof Error) {
    analyticsService.trackException(error, payload as Record<string, unknown>);
  } else if (level === 'warn' || level === 'info') {
    analyticsService.trackEvent(String(payload.event), payload as Record<string, unknown>);
  }
}

class FrontendLogger {
  fatal(message: string, error?: Error | unknown, properties?: LogContext): void {
    write('fatal', message, error, properties);
  }

  error(message: string, error?: Error | unknown, properties?: LogContext): void {
    write('error', message, error, properties);
  }

  warn(message: string, properties?: LogContext): void {
    write('warn', message, undefined, properties);
  }

  info(message: string, properties?: LogContext): void {
    write('info', message, undefined, properties);
  }

  debug(message: string, properties?: LogContext): void {
    write('debug', message, undefined, properties);
  }

  trace(message: string, properties?: LogContext): void {
    write('trace', message, undefined, properties);
  }

  log(level: LogLevel, message: string, properties?: LogContext): void {
    write(level as ClientLogLevel, message, undefined, properties);
  }
}

export const logger = new FrontendLogger();
